import { useEffect, useState } from 'react';
import {
  Drawer,
  Skeleton,
  Empty,
  Button,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import {
  FilePdfOutlined,
  FileMarkdownOutlined,
  ExportOutlined,
  ReloadOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { colors } from '@/theme/tokens';
import { useStore } from '@/stores';
import 'highlight.js/styles/github.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// SourcePreviewDrawer — rich viewer for KB sources (PDF + Markdown)
// ─────────────────────────────────────────────────────────────────────────────
//
// PURPOSE
//   Lets a clinical SME read a knowledge-base source in-place — no download,
//   no new-tab juggling. 90% width drawer keeps page context visible on
//   the left.
//
// VIEWER CHOICES
//   * PDF      — sandboxed iframe pointing at the SAS URL. Modern browsers
//                ship excellent native PDF viewers (zoom, page nav, search,
//                print, download) so we lean on that. Zero added bundle.
//                The blob's Content-Type drives Chrome / Edge / Firefox
//                into PDF mode automatically.
//   * Markdown — react-markdown + remark-gfm + rehype-highlight + rehype-raw.
//                Coverage: GitHub-Flavored tables, task lists, strikethrough,
//                autolinks, code fences with syntax highlighting, embedded
//                HTML (when authors need a callout box), nested lists,
//                blockquotes. We fetch the raw text from the SAS URL, then
//                render it client-side.
//   * Link / unknown — empty state directing the user to "Open in new tab".
//
// NURSE-FRIENDLY UX
//   * Plain title + icon; pass/fail-style noise stays out.
//   * Always-visible "Open in new tab" + "Reload" buttons.
//   * Errors say what to do next, not "fetch failed".
//   * Markdown body uses prose typography — readable line length, generous
//     headings, code blocks visually distinct.
// ─────────────────────────────────────────────────────────────────────────────

export interface PreviewSource {
  kind: 'pdf' | 'markdown' | 'link' | string;
  title: string;
  blobUrl?: string | null;
  url?: string | null;
}

interface Props {
  source: PreviewSource | null;
  onClose: () => void;
}

export function SourcePreviewDrawer({ source, onClose }: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const open = source != null;
  const sourceUrl = source?.blobUrl ?? source?.url ?? null;

  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  // Reset reload key whenever a different source opens.
  useEffect(() => {
    setReloadKey(0);
  }, [source?.title, source?.blobUrl, source?.url]);

  const titleIcon =
    source?.kind === 'pdf' ? (
      <FilePdfOutlined style={{ color: colors.ink[600], fontSize: 18 }} />
    ) : source?.kind === 'markdown' ? (
      <FileMarkdownOutlined style={{ color: colors.violet[600], fontSize: 18 }} />
    ) : (
      <ExportOutlined style={{ color: colors.slate[500], fontSize: 18 }} />
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="90%"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {titleIcon}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {source?.title ?? 'Preview'}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {source?.kind === 'pdf'
                ? 'PDF document'
                : source?.kind === 'markdown'
                ? 'Markdown note'
                : 'Source'}
            </Text>
          </div>
        </div>
      }
      extra={
        <Space size="small">
          <Tooltip title="Reload the preview">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={reload}
              disabled={!sourceUrl}
            />
          </Tooltip>
          <Tooltip title="Open in a new browser tab">
            <Button
              size="small"
              icon={<ExportOutlined />}
              disabled={!sourceUrl}
              onClick={() => {
                if (sourceUrl) window.open(sourceUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              Open in new tab
            </Button>
          </Tooltip>
        </Space>
      }
      styles={{
        body: { padding: 0, background: isDark ? colors.slate[900] : '#FFFFFF' },
      }}
      destroyOnClose
    >
      {!source ? null : !sourceUrl ? (
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: isDark ? colors.slate[300] : colors.slate[600], fontSize: 13 }}>
                This source has no preview URL on file.
              </span>
            }
          />
        </div>
      ) : source.kind === 'pdf' ? (
        // Browser-native PDF viewer. sandbox kept narrow: same-origin so
        // relative resources resolve, popups so the user can right-click →
        // open in new window if they want to. Scripts are NOT enabled.
        <iframe
          key={reloadKey}
          src={sourceUrl}
          title={source.title}
          style={{
            width: '100%',
            height: 'calc(100vh - 120px)',
            border: 'none',
            background: '#525659', // matches Chrome's PDF chrome
          }}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      ) : source.kind === 'markdown' ? (
        <MarkdownView
          key={reloadKey}
          url={sourceUrl}
          isDark={isDark}
          title={source.title}
        />
      ) : (
        // Unknown / link kind — fall back to a friendly empty state.
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ color: isDark ? colors.slate[300] : colors.slate[600], fontSize: 13 }}>
                <div style={{ marginBottom: 6 }}>
                  Preview is not available for this source type.
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Use <b>Open in new tab</b> above to view it.
                </Text>
              </div>
            }
          />
        </div>
      )}
    </Drawer>
  );
}

// ── Markdown view ──────────────────────────────────────────────────────────
//
// Fetches the raw text from the SAS URL, then renders with react-markdown.
// Why fetch instead of iframe? Two reasons:
//   1. Browsers render `.md` blobs as plain-text, not formatted.
//   2. Client-side rendering lets us apply our own typography + theme,
//      so the result looks consistent with the rest of Agent Studio.

function MarkdownView({
  url,
  isDark,
  title,
}: {
  url: string;
  isDark: boolean;
  title: string;
}) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    void (async () => {
      try {
        const r = await fetch(url, { credentials: 'omit' });
        if (!r.ok) throw new Error(`Server returned ${r.status}`);
        const body = await r.text();
        if (!cancelled) setText(body);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[SourcePreviewDrawer] markdown fetch failed:', err);
        if (!cancelled) {
          setError(
            'We could not load this note. Try the reload button, or contact support if it keeps happening.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: isDark ? colors.slate[300] : colors.slate[600], fontSize: 13 }}>
              {error}
            </span>
          }
        />
      </div>
    );
  }

  if (text == null) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 120px)',
        overflow: 'auto',
      }}
    >
      <div
        style={{
          maxWidth: 880,
          margin: '0 auto',
          padding: '32px 40px 80px',
          color: isDark ? colors.slate[100] : colors.slate[800],
          fontSize: 15,
          lineHeight: 1.7,
        }}
        className={isDark ? 'agentstudio-md-dark' : 'agentstudio-md-light'}
      >
        {/* Floating Copy button — small affordance for engineers / SMEs who
            want to grab the raw markdown for editing or pasting. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Tooltip title="Copy raw markdown to clipboard">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                void navigator.clipboard?.writeText(text);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? 'Copied' : 'Copy raw'}
            </Button>
          </Tooltip>
        </div>

        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeHighlight]}
          components={{
            // Slightly tighter heading rhythm than browser defaults — looks
            // better in a side drawer where vertical real estate is precious.
            h1: ({ children }) => (
              <h1 style={{ fontSize: 26, fontWeight: 700, marginTop: 0, marginBottom: 16, lineHeight: 1.25 }}>
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 style={{ fontSize: 21, fontWeight: 700, marginTop: 28, marginBottom: 12, lineHeight: 1.3 }}>
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 style={{ fontSize: 17, fontWeight: 600, marginTop: 22, marginBottom: 10 }}>
                {children}
              </h3>
            ),
            // GitHub-style table chrome — borders + alternating row tint.
            table: ({ children }) => (
              <div style={{ overflowX: 'auto', margin: '14px 0' }}>
                <table
                  style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    fontSize: 14,
                  }}
                >
                  {children}
                </table>
              </div>
            ),
            th: ({ children }) => (
              <th
                style={{
                  border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                  background: isDark ? colors.slate[800] : colors.slate[50],
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontWeight: 600,
                }}
              >
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td
                style={{
                  border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                  padding: '8px 12px',
                  verticalAlign: 'top',
                }}
              >
                {children}
              </td>
            ),
            // Inline code distinct from prose; block code handled by
            // rehype-highlight which adds the right `hljs` classes.
            code: ({ className, children, ...props }) => {
              const isBlock = (className ?? '').startsWith('language-');
              if (isBlock) {
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
              return (
                <code
                  style={{
                    background: isDark ? colors.slate[800] : colors.slate[100],
                    color: isDark ? colors.violet[100] : colors.violet[700],
                    padding: '1px 6px',
                    borderRadius: 4,
                    fontSize: '0.92em',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {children}
                </code>
              );
            },
            pre: ({ children }) => (
              <pre
                style={{
                  background: isDark ? colors.slate[900] : '#F8FAFC',
                  border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  overflow: 'auto',
                  fontSize: 13,
                  lineHeight: 1.55,
                  margin: '14px 0',
                }}
              >
                {children}
              </pre>
            ),
            blockquote: ({ children }) => (
              <blockquote
                style={{
                  borderLeft: `3px solid ${colors.violet[600]}`,
                  background: isDark ? `${colors.violet[700]}15` : colors.violet[50],
                  padding: '6px 14px',
                  margin: '14px 0',
                  color: isDark ? colors.slate[200] : colors.slate[700],
                  borderRadius: '0 6px 6px 0',
                }}
              >
                {children}
              </blockquote>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.violet[600], textDecoration: 'underline' }}
              >
                {children}
              </a>
            ),
            hr: () => (
              <hr
                style={{
                  border: 'none',
                  borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                  margin: '24px 0',
                }}
              />
            ),
          }}
        >
          {text}
        </ReactMarkdown>

        {/* Tiny attribution footer — shows the original filename so the
            reader knows what they're looking at. */}
        <div
          style={{
            marginTop: 40,
            paddingTop: 16,
            borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            fontSize: 11.5,
            color: isDark ? colors.slate[500] : colors.slate[500],
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {title}
        </div>
      </div>
    </div>
  );
}
